import type { ReactNode } from 'react';
import { useCallback } from 'react';

import { Checkboxes } from '#/components/Checkboxes/Checkboxes';
import type { CheckboxOption } from '#/components/Checkboxes/CheckboxOption';

import { Field } from './Field/Field';

type Props = {
  columns?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  disabled?: boolean;
  fullWidth?: boolean;
  name: string;
  label?: ReactNode;
  description?: string;
  options: CheckboxOption[] | readonly CheckboxOption[];
  large?: boolean;
  onUpdateValue?: (value: string[]) => void;
  transformOnChange?: (names: string[]) => string[];
};

/*
 * A field that contains multiple checkboxes. Outputs values as `{ fieldName: [OPTION_NAME] }`.
 */
export function MultiCheckboxField({
  columns,
  disabled,
  fullWidth,
  name,
  label,
  description,
  large,
  onUpdateValue,
  transformOnChange,
  options,
}: Props) {
  const getNewValue = useCallback(
    ({ value, option }: { value: string[]; option: string }) => {
      const newValue = value.includes(option)
        ? value.filter((o) => o !== option)
        : [...value, option];

      return transformOnChange ? transformOnChange(newValue) : newValue;
    },
    [transformOnChange],
  );

  return (
    <Field
      name={name}
      label={label}
      description={description}
      renderInput={({ value = [], id, qaId, hasError, updateValue }) => (
        <Checkboxes
          columns={columns}
          disabled={disabled}
          fullWidth={fullWidth}
          id={id}
          options={options}
          // TODO: Fix this the next time the file is edited.
          // oxlint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          getValue={(optionName) => value.includes(optionName)}
          hasError={hasError}
          onChange={(optionName) => {
            const newValue = getNewValue({
              // TODO: Fix this the next time the file is edited.
              // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
              value: value ?? [],
              option: optionName,
            });

            updateValue(newValue);

            if (onUpdateValue) {
              onUpdateValue(newValue);
            }
          }}
          qaId={qaId}
          large={large}
          getQaId={(checkboxName) => checkboxName}
        />
      )}
    />
  );
}
